import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

const FeedbackPage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    rating: 0,
    subject: '',
    message: '',
    anonymous: false,
    deviceInfo: '',
    stepsToReproduce: '',
    priority: 'medium'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (type === 'radio') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleTypeSelection = (typeValue: string) => {
    setFormData(prev => ({
      ...prev,
      category: typeValue
    }));
  };

  const handleRatingClick = (rating: number) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.category || !formData.message) {
      alert('Lütfen geri bildirim türünü seçin ve mesajınızı yazın.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Backend API'ye feedback gönder
      const response = await axios.post(`${API_BASE_URL}/api/feedbacks`, {
        name: formData.anonymous ? 'Anonim Kullanıcı' : formData.name,
        email: formData.anonymous ? 'anonymous@sobcof.com' : formData.email,
        category: formData.category,
        subject: formData.subject,
        message: formData.message,
        rating: formData.rating,
        anonymous: formData.anonymous,
        deviceInfo: formData.deviceInfo || '',
        stepsToReproduce: formData.stepsToReproduce || '',
        priority: formData.priority || 'medium'
      });

      if (response.data.success) {
        setShowSuccessMessage(true);
        setFormData({
          name: '',
          email: '',
          category: '',
          rating: 0,
          subject: '',
          message: '',
          anonymous: false,
          deviceInfo: '',
          stepsToReproduce: '',
          priority: 'medium'
        });
        // 5 saniye sonra mesajı gizle
        setTimeout(() => setShowSuccessMessage(false), 5000);
      } else {
        alert('Geri bildirim gönderilirken bir hata oluştu: ' + response.data.error);
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      alert('Geri bildirim gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="website-container">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <a href="/" className="logo-link">
              <img src="/assets/LogoBrown.png" alt="SobCof Logo" className="logo-img" />
            </a>
          </div>
          <div className={`nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
            <a href="/" className="nav-link">Ana Sayfa</a>
            <a href="/#features" className="nav-link">Özellikler</a>
            <a href="/#how-it-works" className="nav-link">Nasıl Çalışır</a>
            <a href="/#download" className="nav-link">İndir</a>
            <a href="/contact" className="nav-link">İletişim</a>
            <a href="/cafe-partnership" className="nav-link">Kafe Partnerliği</a>
          </div>
          <div className="nav-toggle" onClick={toggleMobileMenu}>
            <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
          </div>
        </div>
      </nav>

      {/* Feedback Content */}
      <section className="feedback-content">
        <div className="container">
          <div className="feedback-header">
            <h1>Geri Bildirim</h1>
            <p className="feedback-subtitle">
              Görüşleriniz bizim için çok değerli! SobCof'u daha iyi hale getirmek için 
              fikirlerinizi, önerilerinizi ve deneyimlerinizi paylaşın.
            </p>
          </div>

          <div className="feedback-types">
            <div
              className={`feedback-type-card ${formData.category === 'suggestion' ? 'active' : ''}`}
              data-type="suggestion"
              onClick={() => handleTypeSelection('suggestion')}
            >
              <div className="type-icon">
                <i className="fas fa-lightbulb"></i>
              </div>
              <h3>Öneri</h3>
              <p>Yeni özellik önerileri</p>
            </div>

            <div
              className={`feedback-type-card ${formData.category === 'bug' ? 'active' : ''}`}
              data-type="bug"
              onClick={() => handleTypeSelection('bug')}
            >
              <div className="type-icon">
                <i className="fas fa-bug"></i>
              </div>
              <h3>Hata Bildirimi</h3>
              <p>Teknik sorunlar</p>
            </div>

            <div
              className={`feedback-type-card ${formData.category === 'improvement' ? 'active' : ''}`}
              data-type="improvement"
              onClick={() => handleTypeSelection('improvement')}
            >
              <div className="type-icon">
                <i className="fas fa-arrow-up"></i>
              </div>
              <h3>İyileştirme</h3>
              <p>Mevcut özelliklerin geliştirilmesi</p>
            </div>

            <div
              className={`feedback-type-card ${formData.category === 'review' ? 'active' : ''}`}
              data-type="review"
              onClick={() => handleTypeSelection('review')}
            >
              <div className="type-icon">
                <i className="fas fa-star"></i>
              </div>
              <h3>Değerlendirme</h3>
              <p>Genel deneyim paylaşımı</p>
            </div>

            <div
              className={`feedback-type-card ${formData.category === 'story' ? 'active' : ''}`}
              data-type="story"
              onClick={() => handleTypeSelection('story')}
            >
              <div className="type-icon">
                <i className="fas fa-heart"></i>
              </div>
              <h3>Başarı Hikayesi</h3>
              <p>Tanışma deneyimleriniz</p>
            </div>
          </div>

          <div className="feedback-main">
            <div className="feedback-form-section">

              {/* Success Message */}
              {showSuccessMessage && (
                <div className="success-message" style={{
                  background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                  color: 'white',
                  padding: '20px',
                  borderRadius: '10px',
                  marginBottom: '30px',
                  textAlign: 'center',
                  boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)',
                  animation: 'slideDown 0.5s ease-out'
                }}>
                  <div style={{ fontSize: '24px', marginBottom: '10px' }}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '20px' }}>
                    Geri bildiriminiz başarıyla gönderildi!
                  </h3>
                  <p style={{ margin: '0', opacity: '0.9' }}>
                    Teşekkür ederiz. En kısa sürede değerlendireceğiz.
                  </p>
                </div>
              )}

              <div className="feedback-form-header">
                <h3>
                  <i className="fas fa-comment-dots"></i>
                  Geri Bildiriminizi Paylaşın
                </h3>
                <p>Görüşleriniz bizim için çok değerli. Lütfen deneyiminizi bizimle paylaşın.</p>
              </div>

              <form className="feedback-form" onSubmit={handleSubmit}>


                <div className="form-section">
                  <div className="section-header">
                    <i className="fas fa-user"></i>
                    <span>İletişim Bilgileri (Opsiyonel)</span>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">
                        <i className="fas fa-user-circle"></i>
                        Adınız
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Anonim kalmak isterseniz boş bırakabilirsiniz"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">
                        <i className="fas fa-envelope"></i>
                        E-posta
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Yanıt almak isterseniz e-posta adresinizi yazın"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="section-header">
                    <i className="fas fa-edit"></i>
                    <span>Geri Bildirim Detayları</span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="title">
                      <i className="fas fa-heading"></i>
                      Başlık *
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      placeholder="Geri bildiriminizin kısa ve öz başlığı"
                      maxLength={100}
                    />
                  </div>

                  {formData.category === 'review' && (
                    <div className="form-group rating-group">
                      <label>
                        <i className="fas fa-star"></i>
                        SobCof'u nasıl değerlendiriyorsunuz?
                      </label>
                      <div className="rating-container">
                        <div className="rating-stars">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              className={`star-btn ${formData.rating >= star ? 'active' : ''}`}
                              onClick={() => handleRatingClick(star)}
                            >
                              <i className="fas fa-star"></i>
                            </button>
                          ))}
                        </div>
                        <span className="rating-text">
                          {formData.rating === 0 ? 'Puan seçin' :
                           formData.rating === 1 ? '⭐ Çok kötü' :
                           formData.rating === 2 ? '⭐⭐ Kötü' :
                           formData.rating === 3 ? '⭐⭐⭐ Orta' :
                           formData.rating === 4 ? '⭐⭐⭐⭐ İyi' : '⭐⭐⭐⭐⭐ Mükemmel'}
                        </span>
                      </div>
                    </div>
                  )}

                  {formData.category === 'bug' && (
                    <div className="bug-details-section">
                      <div className="form-group">
                        <label htmlFor="deviceInfo">
                          <i className="fas fa-mobile-alt"></i>
                          Cihaz Bilgileri
                        </label>
                        <input
                          type="text"
                          id="deviceInfo"
                          name="deviceInfo"
                          placeholder="Örn: iPhone 13, iOS 16.0 / Samsung Galaxy S21, Android 12"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="reproductionSteps">
                          <i className="fas fa-list-ol"></i>
                          Hatayı Nasıl Tekrarlarım?
                        </label>
                        <textarea
                          id="reproductionSteps"
                          name="reproductionSteps"
                          rows={3}
                          placeholder="1. Uygulamayı açın&#10;2. ... sayfasına gidin&#10;3. ... butonuna tıklayın&#10;4. Hata oluşur"
                        />
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="message">
                      <i className="fas fa-comment-alt"></i>
                      Detaylı Açıklama *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder="Geri bildiriminizi mümkün olduğunca detaylı şekilde açıklayın. Ne kadar detay verirseniz, size o kadar iyi yardımcı olabiliriz."
                      maxLength={2000}
                    />
                    <div className="char-count">
                      {formData.message.length}/2000 karakter
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="priority">
                      <i className="fas fa-exclamation-triangle"></i>
                      Öncelik Seviyesi
                    </label>
                    <select id="priority" name="priority" defaultValue="medium">
                      <option value="low">🟢 Düşük - Acil değil</option>
                      <option value="medium">🟡 Orta - Normal öncelik</option>
                      <option value="high">🟠 Yüksek - Önemli</option>
                      <option value="urgent">🔴 Acil - Hemen çözülmeli</option>
                    </select>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="submit-btn primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <div className="spinner"></div>
                        <span>Gönderiliyor...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane"></i>
                        <span>Geri Bildirimi Gönder</span>
                      </>
                    )}
                  </button>

                  <div className="form-note">
                    <i className="fas fa-info-circle"></i>
                    <span>Geri bildiriminizi 24-48 saat içinde değerlendiriyoruz</span>
                  </div>
                </div>
              </form>
            </div>

            <div className="feedback-info-section">
              <div className="info-card">
                <h3><i className="fas fa-lightbulb"></i> Özellik Önerileri</h3>
                <p>
                  Yeni özellik fikirlerinizi paylaşın! En çok talep edilen özellikler 
                  öncelikli olarak geliştirilir.
                </p>
              </div>

              <div className="info-card">
                <h3><i className="fas fa-bug"></i> Hata Bildirimi</h3>
                <p>
                  Karşılaştığınız teknik sorunları bildirin. Hızlı çözüm için 
                  detaylı açıklama yapın.
                </p>
              </div>

              <div className="info-card">
                <h3><i className="fas fa-heart"></i> Deneyim Paylaşımı</h3>
                <p>
                  SobCof ile yaşadığınız güzel anıları ve başarı hikayelerini 
                  bizimle paylaşın!
                </p>
              </div>

              <div className="info-card">
                <h3><i className="fas fa-clock"></i> Yanıt Süresi</h3>
                <p>
                  Geri bildirimlerinizi 24-48 saat içinde değerlendiriyoruz. 
                  Önemli konularda daha hızlı yanıt veriyoruz.
                </p>
              </div>
            </div>
          </div>


        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <a href="/" className="logo-link">
                  <img src="/assets/LogoWhite.png" alt="SobCof Logo" />
                </a>
              </div>
              <p className="footer-description">
                Kahve severler için sosyal keşif platformu. En iyi kafeleri keşfet, yeni insanlarla tanış.
              </p>
              <div className="social-links">
                <a href="https://instagram.com/sobcof" title="Instagram" target="_blank" rel="noopener noreferrer"><i className="fab fa-instagram"></i></a>
                <a href="https://tiktok.com/@sobcof" title="TikTok" target="_blank" rel="noopener noreferrer"><i className="fab fa-tiktok"></i></a>
                <a href="https://twitter.com/sobcof" title="Twitter" target="_blank" rel="noopener noreferrer"><i className="fab fa-twitter"></i></a>
                <a href="https://youtube.com/@sobcof" title="YouTube" target="_blank" rel="noopener noreferrer"><i className="fab fa-youtube"></i></a>
              </div>
            </div>

            <div className="footer-section">
              <h4>Ürün</h4>
              <ul>
                <li><a href="/#features">Özellikler</a></li>
                <li><a href="/#premium">Premium</a></li>
                <li><a href="/#download">İndir</a></li>
                <li><a href="/#security">Güvenlik</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Destek</h4>
              <ul>
                <li><a href="/help">Yardım Merkezi</a></li>
                <li><a href="/contact">İletişim</a></li>
                <li><a href="/feedback">Geri Bildirim</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Yasal</h4>
              <ul>
                <li><a href="/privacy">Gizlilik Politikası</a></li>
                <li><a href="/terms">Kullanım Koşulları</a></li>
                <li><a href="/cookies">Çerez Politikası</a></li>
                <li><a href="/gdpr">KVKK</a></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2026 SobCof. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FeedbackPage;
